import { createFile, type MP4BoxBuffer } from "mp4box";
import { useEffect, useState } from "react";

const READ_CHUNK_SIZE = 1024 * 1024;
const MAX_METADATA_SCAN_BYTES = 64 * 1024 * 1024;
const TAIL_METADATA_SCAN_BYTES = 16 * 1024 * 1024;
const HEAD_METADATA_SCAN_BYTES =
  MAX_METADATA_SCAN_BYTES - TAIL_METADATA_SCAN_BYTES;

type Mp4Info = {
  videoTracks?: Array<{
    duration: number;
    timescale: number;
    nb_samples: number;
    track_width?: number;
    track_height?: number;
  }>;
};

/**
 * Calculates frame rate and duration from the first usable MP4 video track.
 *
 * @param info - MP4Box metadata containing zero or more video tracks.
 * @returns Null metadata when no track has valid timing/sample data, or a
 * finite positive value cannot be calculated. This helper does not throw for
 * incomplete metadata.
 */
export function videoTrackMetadata(info: Mp4Info) {
  const track = info.videoTracks?.find(
    (candidate) =>
      candidate.nb_samples > 0 &&
      candidate.duration > 0 &&
      candidate.timescale > 0,
  );
  if (!track) {
    return { fps: null, durationSeconds: null, width: null, height: null };
  }

  const durationSeconds = track.duration / track.timescale;
  const trackWidth = track.track_width;
  const trackHeight = track.track_height;
  const fps = track.nb_samples / durationSeconds;
  return {
    fps: Number.isFinite(fps) && fps > 0 ? fps : null,
    durationSeconds:
      Number.isFinite(durationSeconds) && durationSeconds > 0
        ? durationSeconds
        : null,
    width:
      typeof trackWidth === "number" &&
      Number.isSafeInteger(trackWidth) &&
      trackWidth > 0
        ? trackWidth
        : null,
    height:
      typeof trackHeight === "number" &&
      Number.isSafeInteger(trackHeight) &&
      trackHeight > 0
        ? trackHeight
        : null,
  };
}

async function readVideoFrameRate(
  file: File,
  isCancelled: () => boolean,
): Promise<{
  fps: number | null;
  durationSeconds: number | null;
  width: number | null;
  height: number | null;
} | null> {
  if (file.size === 0) {
    return { fps: null, durationSeconds: null, width: null, height: null };
  }

  const parser = createFile();
  let parsed:
    | {
        fps: number | null;
        durationSeconds: number | null;
        width: number | null;
        height: number | null;
      }
    | undefined;
  let parsingError: Error | undefined;
  parser.onReady = (info) => {
    parsed = videoTrackMetadata(info);
  };
  parser.onError = (_module, message) => {
    parsingError = new Error(message);
  };

  async function appendChunk(start: number, end: number) {
    const chunk = (await file.slice(start, end).arrayBuffer()) as MP4BoxBuffer;
    chunk.fileStart = start;
    parser.appendBuffer(chunk, end === file.size);
  }

  try {
    let offset = 0;
    while (
      offset < file.size &&
      offset < HEAD_METADATA_SCAN_BYTES &&
      !isCancelled()
    ) {
      const end = Math.min(
        offset + READ_CHUNK_SIZE,
        HEAD_METADATA_SCAN_BYTES,
        file.size,
      );
      await appendChunk(offset, end);
      offset = end;
      if (parser.readySent || parsingError) break;
    }

    // Camera recordings commonly put `moov` at the end. Probe the tail within
    // the same fixed memory budget rather than retaining an entire large file.
    const tailStart = Math.max(offset, file.size - TAIL_METADATA_SCAN_BYTES);
    offset = tailStart;
    while (
      offset < file.size &&
      !parser.readySent &&
      !parsingError &&
      !isCancelled()
    ) {
      const end = Math.min(offset + READ_CHUNK_SIZE, file.size);
      await appendChunk(offset, end);
      offset = end;
    }

    if (isCancelled()) return null;
    if (parsingError) throw parsingError;
    return (
      parsed ?? { fps: null, durationSeconds: null, width: null, height: null }
    );
  } finally {
    parser.flush();
  }
}

/**
 * Reads MP4 metadata for a selected file and exposes its frame rate and duration.
 *
 * Passing `null` resets the hook to its idle state. A new file clears prior
 * values, sets `isDetecting` while metadata is read, then populates `fps` and
 * `durationSeconds` when parsing succeeds. Missing frame-rate metadata leaves
 * `fps` as `null` and sets `error`; parser failures expose their message when
 * available. Results from a superseded file are ignored.
 *
 * @returns Detection state. `fps` and `durationSeconds` are `null` before a
 * successful parse, `isDetecting` is `true` only while the active file is
 * being read, and `error` is `null` unless metadata cannot be read.
 */
export function useVideoFrameRate(file: File | null) {
  const [fps, setFps] = useState<number | null>(null);
  const [durationSeconds, setDurationSeconds] = useState<number | null>(null);
  const [width, setWidth] = useState<number | null>(null);
  const [height, setHeight] = useState<number | null>(null);
  const [isDetecting, setIsDetecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setFps(null);
      setDurationSeconds(null);
      setWidth(null);
      setHeight(null);
      setIsDetecting(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setFps(null);
    setDurationSeconds(null);
    setWidth(null);
    setHeight(null);
    setError(null);
    setIsDetecting(true);

    void readVideoFrameRate(file, () => cancelled)
      .then((result) => {
        if (cancelled) return;
        if (!result) return;
        const {
          fps: detectedFps,
          durationSeconds: detectedDurationSeconds,
          width: detectedWidth,
          height: detectedHeight,
        } = result;
        setFps(detectedFps);
        setDurationSeconds(detectedDurationSeconds);
        setWidth(detectedWidth);
        setHeight(detectedHeight);
        if (detectedFps === null) {
          setError("Unable to read the video frame rate from its metadata.");
        }
      })
      .catch((detectionError: unknown) => {
        if (cancelled) return;
        setError(
          detectionError instanceof Error
            ? detectionError.message
            : "Unable to read the video frame rate from its metadata.",
        );
      })
      .finally(() => {
        if (!cancelled) setIsDetecting(false);
      });

    return () => {
      cancelled = true;
    };
  }, [file]);

  return { fps, durationSeconds, width, height, isDetecting, error };
}
